/**
 * Additive UI: buyer / listing agent can propose a deal-scoped dictionary clause.
 * Does not remove or replace any existing negotiation controls.
 */

import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'sonner';

import { useProposeClauseDictionaryMutation } from '@/apis/extended/clauseDictionaryApi';

type Props = {
  contractId: string;
};

export function ProposeDictionaryClauseCard({ contractId }: Props) {
  const [propose, { isLoading }] = useProposeClauseDictionaryMutation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [defaultValue, setDefaultValue] = useState('');

  const onSubmit = async () => {
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    try {
      await propose({
        contractId,
        title: title.trim(),
        description: description.trim(),
        defaultValue,
        propertyTypes: ['Commercial'],
      }).unwrap();
      toast.success('Clause proposed — admin can approve into the global pack');
      setTitle('');
      setDescription('');
      setDefaultValue('');
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'data' in e
          ? String((e as { data?: { error?: string } }).data?.error)
          : 'Could not propose clause';
      toast.error(msg || 'Could not propose clause');
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Propose a custom clause
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Adds a deal-scoped dictionary clause for negotiation. Existing
          residential clauses stay in place.
        </Typography>
        <Stack gap={1.25}>
          <TextField
            size="small"
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <TextField
            size="small"
            label="Description"
            multiline
            minRows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <TextField
            size="small"
            label="Proposed text / value"
            multiline
            minRows={2}
            value={defaultValue}
            onChange={(e) => setDefaultValue(e.target.value)}
          />
          <Box>
            <Button
              variant="outlined"
              onClick={() => void onSubmit()}
              disabled={isLoading}
            >
              Propose clause
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
